<template>
  <Suspense>
    <template #default>
      <div class="content-grid content-grid--subpage">
        <Breadcrumbs :title="t(`routes.admin.children.organizations.merge.name`)"/>
        <div class="content-grid__header">
          <BackLinkTitle :title="t('routes.admin.children.organizations.merge.name')"/>
        </div>
        <Card>
          <form class="form" @submit.prevent="handleSubmit">
            <FormSearchableSelect
              v-if="organizationOptions"
              v-model="sourceOrganizationId"
              :label="t('organization.toDelete')"
              :options="organizationOptions"
              name="sourceOrganization"
            />
            <FormSearchableSelect
              v-if="organizationOptions"
              v-model="destinationOrganizationId"
              :label="t('organization.toKeep')"
              :options="organizationOptions"
              name="destinationOrganization"
            />
            <button class="form__submit btn btn--fullscreen">
              {{ t('global.merge') }}
            </button>
          </form>
        </Card>
      </div>
    </template>
    <template #fallback>
      <Loader/>
    </template>
  </Suspense>
</template>

<script lang="ts" setup>
import Loader from "@/components/layout/Loader.vue";
import {useI18n} from "vue3-i18n";
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import BackLinkTitle from "@/components/layout/BackLinkTitle.vue";
import {useOrganizationService} from "@/inversify.config";
import {onMounted, ref} from "vue";
import Card from "@/components/layout/Card.vue";
import {IOrganization} from "@/types/entities/organization";
import {notifyError, notifySuccess} from "@/notify";
import {FormOption} from "@/types";
import FormSearchableSelect from "@/components/forms/FormSearchableSelect.vue";

const {t} = useI18n()
const organizationService = useOrganizationService();

const organizationOptions = ref<FormOption[]>();
const sourceOrganizationId = ref<string>();
const destinationOrganizationId = ref<string>();
const organizationIsLoading = ref(false);

onMounted(() => {
  loadOrganizations()
})

async function loadOrganizations() {
  organizationIsLoading.value = true;
  let organizations = await organizationService.getAllOrganizations()
  organizationOptions.value = organizations
    .map((x: IOrganization) => ({
      name: x.id.toString(),
      label: x.name
    }))
    .sortByLabel()
  organizationIsLoading.value = false;
}

async function handleSubmit() {
  if (!sourceOrganizationId.value) {
    notifyError(t('validation.organization.merge.chooseOrganizationToDelete'))
    return;
  }

  if (!destinationOrganizationId.value) {
    notifyError(t('validation.organization.merge.chooseOrganizationToKeep'))
    return;
  }

  if (sourceOrganizationId.value === destinationOrganizationId.value) {
    notifyError(t('validation.organization.merge.organizationToDeleteAndToKeepCannotBeTheSame'))
    return;
  }

  let succeededOrNotResponse = await organizationService
    .mergeTwoOrganizations(sourceOrganizationId.value, destinationOrganizationId.value)
  if (succeededOrNotResponse && succeededOrNotResponse.succeeded) {
    notifySuccess(t('validation.organization.merge.success'))
  } else {
    let errorMessages = succeededOrNotResponse.getErrorMessages(
        'validation.organization.merge', 'validation.organization.merge.errorOccurred');
    notifyError(errorMessages[0])
  }
}
</script>
